import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import ViewerSidebar from "../components/ViewerSidebar";
import api from "../services/api";
import { downloadEsmpFile, getDownloadEndpoint } from "../utils/fileDownload";

const toCsvValue = (value) => {
  const str = String(value ?? "");
  if (/[",\n]/.test(str)) return `"${str.replaceAll('"', '""')}"`;
  return str;
};

export default function ViewerDownloadAllESMPs() {
  const [search, setSearch] = useState("");
  const [esmps, setEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchEsmps = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/esmp/list");
      const payload = res.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.esmps)
          ? payload.esmps
          : [];
      setEsmps(list);
    } catch (error) {
      console.error("Failed to fetch ESMP list:", error);
      setEsmps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEsmps();
  }, [fetchEsmps]);

  const filtered = useMemo(() => {
    return esmps.filter((item) => {
      const q = search.toLowerCase();
      return (
        (item.esmp_id || "").toLowerCase().includes(q) ||
        (item.project_name || "").toLowerCase().includes(q) ||
        (item.district || "").toLowerCase().includes(q)
      );
    });
  }, [esmps, search]);

  const downloadable = useMemo(
    () => filtered.filter((item) => !!item.file_path),
    [filtered]
  );

  const downloadCsv = () => {
    const rows = [
      ["esmp_id", "project_name", "district", "status", "file_name", "download_path"],
      ...filtered.map((item) => [
        item.esmp_id,
        item.project_name,
        item.district,
        item.status,
        item.file_name,
        getDownloadEndpoint(item.id, "viewer"),
      ]),
    ];

    const csv = rows.map((row) => row.map(toCsvValue).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `esmps_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const downloadAllFiles = async () => {
    if (downloadable.length === 0) return;

    setDownloading(true);
    const toastId = toast.loading("Starting downloads...");
    try {
      for (const item of downloadable) {
        // eslint-disable-next-line no-await-in-loop
        await downloadEsmpFile(item, "viewer");
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 350));
      }
      toast.success("Downloads started", { id: toastId });
    } catch (error) {
      console.error("Bulk download failed:", error);
      toast.error("Some downloads could not be started.", { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const downloadSingleFile = async (item) => {
    if (!item?.file_path) return;

    setDownloadingId(item.id);
    const toastId = toast.loading("Preparing download...");
    try {
      await downloadEsmpFile(item, "viewer");
      toast.success("Download started", { id: toastId });
    } catch (error) {
      console.error("Single download failed:", error);
      toast.error("Download failed. Please try again.", { id: toastId });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ViewerSidebar />

      <main className="flex-1 p-6 space-y-6">
        <div className="bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Download All ESMPs</h2>
          <p className="text-sm mt-1">
            Download ESMP documents or export a CSV of all records in the system
          </p>
        </div>

        <div className="bg-white p-4 rounded-xl shadow grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ESMP ID, project, district"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-600 outline-none"
            />
          </div>

          <div className="flex items-end gap-3 md:col-span-2">
            <button
              onClick={fetchEsmps}
              className="flex-1 px-4 py-2 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800"
            >
              Refresh
            </button>
            <button
              onClick={downloadCsv}
              className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-lg font-semibold hover:bg-black"
            >
              Download CSV
            </button>
            <button
              onClick={downloadAllFiles}
              disabled={downloading || downloadable.length === 0}
              className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white ${
                downloading || downloadable.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-700 hover:bg-blue-800"
              }`}
              title={
                downloadable.length === 0
                  ? "No ESMPs with attached files in the current filter"
                  : "Downloads all documents in the current filter"
              }
            >
              {downloading ? "Downloading..." : `Download Files (${downloadable.length})`}
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          {loading ? (
            <p className="text-gray-500">Loading ESMPs...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-6">No ESMPs found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left">ESMP ID</th>
                    <th className="border px-3 py-2 text-left">Project</th>
                    <th className="border px-3 py-2 text-left">District</th>
                    <th className="border px-3 py-2 text-left">Status</th>
                    <th className="border px-3 py-2 text-left">Document</th>
                    <th className="border px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id} className="hover:bg-green-50">
                      <td className="border px-3 py-2 font-medium text-blue-700">
                        {item.esmp_id || "N/A"}
                      </td>
                      <td className="border px-3 py-2">{item.project_name || "N/A"}</td>
                      <td className="border px-3 py-2">{item.district || "N/A"}</td>
                      <td className="border px-3 py-2">{item.status || "N/A"}</td>
                      <td className="border px-3 py-2">
                        {item.file_path ? (
                          <button
                            type="button"
                            onClick={() => downloadSingleFile(item)}
                            className="text-blue-600 hover:underline"
                          >
                            {item.file_name || "Download document"}
                          </button>
                        ) : (
                          <span className="text-gray-500">No file</span>
                        )}
                      </td>
                      <td className="border px-3 py-2">
                        {item.file_path ? (
                          <button
                            type="button"
                            onClick={() => downloadSingleFile(item)}
                            disabled={downloadingId === item.id}
                            className={`inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-semibold text-white ${
                              downloadingId === item.id
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-700 hover:bg-blue-800"
                            }`}
                            title="Download this document"
                          >
                            {downloadingId === item.id ? "Preparing..." : "Download"}
                          </button>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
